import {Injectable, OnDestroy} from '@angular/core';
import {Location} from '@angular/common';
import {Title} from '@angular/platform-browser';
import {NavigationEnd, Router} from '@angular/router';
import {Store, select} from '@ngrx/store';
import {Observable, Subject, combineLatest, merge} from 'rxjs';
import {distinctUntilChanged, filter, map, shareReplay, startWith, takeUntil} from 'rxjs/operators';
import {getLoggedInUser} from '@app/stores/authentication/authentication.selectors';
import {EndImpersonation, LogoutUser} from '@app/stores/authentication/authentication.actions';
import {NavigationAction} from '@app/modules/shared/models/navigation';
import {bookmarkKey, normalizeInAppUrl} from '@app/_helpers/navigation-preferences';
import {LoaderService} from '@app/services/loader.service';
import {BookmarkResult, NavigationStateService} from '@app/services/navigation-state.service';
import {ProjectContextService} from '@app/services/project-context.service';
import {NavCrumbService} from '@app/services/nav-crumb.service';
import {NavLayoutService} from '@app/services/nav-layout.service';
import {ListUrlStateService} from '@app/services/list-url-state.service';
import {
  NavCrumb, NavTrail, ResolvedBookmark, ResolvedNavNode, buildBreadcrumb, findActiveTrail, findNode, parseInAppUrl,
  resolveBookmarks, resolveTree,
} from '@app/_components/navigation/model/nav-tree';
import {NavSearchEntry, buildSearchIndex} from '@app/_components/navigation/model/nav-search';

export interface CurrentPageBookmark {
  /** What the toolbar star would save for the page on screen, or null when it cannot. */
  kind: 'menu' | 'page' | null;
  menuId?: string;
  url?: string;
  /** The name to offer: the entry's label, or the breadcrumb for any other page. */
  label?: string;
  icon?: string | null;
  bookmarked: boolean;
  /** The name it is bookmarked under, when it is. */
  currentLabel?: string;
}

const DEFAULT_TITLE = 'Snöball - Influencer marketing';

/**
 * Everything the navigation components render, derived once: the menus resolved for the
 * project on screen, which entry the page belongs to, the breadcrumb, the favorites and the
 * search index - plus the commands the menus run.
 */
@Injectable({providedIn: 'root'})
export class NavigationMenuService implements OnDestroy {
  private readonly destroyed = new Subject<void>();

  readonly url$: Observable<string> = this.router.events.pipe(
    filter((event): event is NavigationEnd => event instanceof NavigationEnd),
    map((event) => event.urlAfterRedirects),
    startWith(this.router.url),
    distinctUntilChanged(),
    shareReplay({bufferSize: 1, refCount: false})
  );

  readonly isInfluencer$: Observable<boolean> = this.store.pipe(
    select(getLoggedInUser),
    map((user: any) => String(user?.user_type) === '2'),
    distinctUntilChanged(),
    shareReplay({bufferSize: 1, refCount: false})
  );

  /** Null when the server sent no menu: the shell shows "Menu unavailable" rather than nothing. */
  readonly available$: Observable<boolean> = this.navigationState.navigation$.pipe(
    map((navigation) => !!navigation), distinctUntilChanged());

  readonly main$: Observable<ResolvedNavNode[]> = combineLatest([
    this.navigationState.navigation$,
    this.projectContext.context$,
    this.navigationState.menuMode$,
  ]).pipe(
    map(([navigation, context, mode]) => resolveTree(navigation?.main,
      {projectId: context.inProjectContext ? context.projectId : null, mode})),
    shareReplay({bufferSize: 1, refCount: false})
  );

  readonly user$: Observable<ResolvedNavNode[]> = this.navigationState.navigation$.pipe(
    map((navigation) => resolveTree(navigation?.user, {projectId: null, mode: 'side', menu: 'user'})),
    shareReplay({bufferSize: 1, refCount: false})
  );

  /** Every entry a menu bookmark may name, whatever project is open. */
  private readonly bookmarkable$: Observable<ResolvedNavNode[]> = this.navigationState.navigation$.pipe(
    map((navigation) => [
      ...resolveTree(navigation?.main, {projectId: null, mode: 'side'}),
      ...resolveTree(navigation?.user, {projectId: null, mode: 'side', menu: 'user'}),
    ]),
    shareReplay({bufferSize: 1, refCount: false})
  );

  readonly trail$: Observable<NavTrail | null> = combineLatest([
    this.main$, this.user$, this.url$, this.navLayout.routeData$,
  ]).pipe(
    map(([main, user, url, routeData]) => findActiveTrail([...main, ...user], url, routeData.navParent)),
    shareReplay({bufferSize: 1, refCount: false})
  );

  /** The active entry and every group above it. */
  readonly activeIds$: Observable<Set<string>> = this.trail$.pipe(
    map((trail) => new Set(trail ? [trail.node.id, ...trail.groups.map((group) => group.id)] : [])),
    shareReplay({bufferSize: 1, refCount: false})
  );

  readonly favorites$: Observable<ResolvedBookmark[]> = combineLatest([
    this.navigationState.bookmarks$, this.bookmarkable$,
  ]).pipe(
    map(([bookmarks, bookmarkable]) => resolveBookmarks(bookmarks, bookmarkable)),
    shareReplay({bufferSize: 1, refCount: false})
  );

  /** Bookmarks the current menus cannot show - an admin's entries while impersonating a customer. */
  readonly hiddenFavoriteCount$: Observable<number> = combineLatest([
    this.navigationState.bookmarks$, this.favorites$,
  ]).pipe(map(([bookmarks, favorites]) => bookmarks.length - favorites.length));

  /** Bookmark keys, for the stars beside menu entries. */
  readonly starredKeys$: Observable<Set<string>> = this.navigationState.bookmarks$.pipe(
    map((bookmarks) => new Set(bookmarks.map(bookmarkKey))),
    shareReplay({bufferSize: 1, refCount: false})
  );

  readonly searchIndex$: Observable<NavSearchEntry[]> = combineLatest([this.main$, this.user$, this.favorites$]).pipe(
    map(([main, user, favorites]) => buildSearchIndex(main, user, favorites)),
    shareReplay({bufferSize: 1, refCount: false})
  );

  readonly breadcrumb$: Observable<NavCrumb[]> = combineLatest([
    this.trail$, this.projectContext.context$, this.navCrumb.leaf$, this.navLayout.routeData$,
    this.bookmarkable$, this.isInfluencer$,
  ]).pipe(
    map(([trail, context, leaf, routeData, bookmarkable, isInfluencer]) => buildBreadcrumb({
      trail,
      project: context.inProjectContext
        ? {id: context.projectId, title: context.project?.title || null} : null,
      projectHome: context.inProjectContext ? this.projectContext.projectHome(context.projectId, isInfluencer) : null,
      projectsNode: findNode(bookmarkable, 'projects'),
      leaf: leaf || routeData.navTitle,
    })),
    shareReplay({bufferSize: 1, refCount: false})
  );

  /** The address as shown, including a list page's filters rewritten in place. */
  private readonly shownUrl$: Observable<string> = merge(this.url$, this.listUrlState.changes$).pipe(
    map(() => this.location.path()),
    distinctUntilChanged()
  );

  readonly currentPage$: Observable<CurrentPageBookmark> = combineLatest([
    this.shownUrl$, this.trail$, this.breadcrumb$, this.navigationState.bookmarks$,
  ]).pipe(
    map(([url]) => this.currentPageBookmark(url)),
    shareReplay({bufferSize: 1, refCount: false})
  );

  private lastTrail: NavTrail | null = null;
  private lastCrumbs: NavCrumb[] = [];

  constructor(
    private router: Router,
    private location: Location,
    private store: Store<any>,
    private title: Title,
    private loaderService: LoaderService,
    private navigationState: NavigationStateService,
    private projectContext: ProjectContextService,
    private navCrumb: NavCrumbService,
    private navLayout: NavLayoutService,
    private listUrlState: ListUrlStateService,
  ) {
    this.trail$.pipe(takeUntil(this.destroyed)).subscribe((trail) => this.lastTrail = trail);
    this.breadcrumb$.pipe(takeUntil(this.destroyed)).subscribe((crumbs) => {
      this.lastCrumbs = crumbs;
      this.updateDocumentTitle(crumbs);
    });
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }

  /** The key a node's star toggles: its menu id, or - for one project's page - its address. */
  starKey(node: ResolvedNavNode): string | null {
    if (node.type !== 'link') return null;
    if (node.context === 'project') {
      const url = this.nodeUrl(node);
      return url ? `page:${url}` : null;
    }
    return `menu:${node.id}`;
  }

  toggleStar(node: ResolvedNavNode): BookmarkResult {
    if (node.type !== 'link') return 'invalid';
    if (node.context !== 'project') return this.navigationState.toggleMenuBookmark(node.id);
    const project = this.projectContext.context.project;
    const label = [project?.title || null, node.label].filter(Boolean).join(' › ');
    return this.navigationState.togglePageBookmark(this.nodeUrl(node), label, node.icon);
  }

  /**
   * The toolbar star: bookmark the page on screen under the name the person gave it - as a
   * menu bookmark when the page is exactly an entry's own, else by its address.
   */
  bookmarkCurrentPage(label: string): BookmarkResult {
    const candidate = this.currentPageNow();
    if (candidate.bookmarked) return 'invalid';
    if (candidate.kind === 'menu') {
      // The entry's own label is not stored as a name, so a later rename of the entry shows.
      const own = label && label.trim() !== candidate.label ? label : null;
      return this.navigationState.toggleMenuBookmark(candidate.menuId, own);
    }
    if (candidate.kind === 'page') {
      return this.navigationState.togglePageBookmark(candidate.url, label || candidate.label, candidate.icon);
    }
    return 'invalid';
  }

  renameCurrentPage(label: string): void {
    const key = this.currentPageKey();
    if (key) this.navigationState.renameBookmark(key, label);
  }

  removeCurrentPage(): void {
    const key = this.currentPageKey();
    if (key) this.navigationState.removeBookmark(key);
  }

  /**
   * The page on screen as the address bar shows it now - list pages keep their filters there
   * without a router navigation (ListUrlStateService), so the router's URL can lag behind.
   */
  currentPageNow(): CurrentPageBookmark {
    return this.currentPageBookmark(this.location.path());
  }

  private currentPageKey(): string | null {
    const candidate = this.currentPageNow();
    if (candidate.kind === 'menu') return `menu:${candidate.menuId}`;
    if (candidate.kind === 'page') return `page:${candidate.url}`;
    return null;
  }

  runAction(action: NavigationAction): void {
    if (action === 'logout') this.logout();
    if (action === 'stopImpersonation') this.store.dispatch(EndImpersonation());
    if (action === 'menuMode') {
      this.navigationState.setMenuMode(this.navigationState.preferences.menuMode === 'side' ? 'top' : 'side');
    }
  }

  logout(): void {
    if (!localStorage.getItem('currentUser')) return;
    this.loaderService.unsubscribe();
    this.store.dispatch(LogoutUser());
    // No returnUrl: after an intentional logout, sign-in lands on the role's own home.
    this.router.navigate(['/login']);
  }

  private nodeUrl(node: ResolvedNavNode): string | null {
    if (!node.path) return null;
    const query = node.queryParams ? new URLSearchParams(node.queryParams).toString() : '';
    return normalizeInAppUrl(node.path + (query ? `?${query}` : ''));
  }

  private currentPageBookmark(url: string): CurrentPageBookmark {
    const trail = this.lastTrail;
    // Exactly the entry's page only if the address carries nothing beyond the entry's own
    // query - a list with its filters in the address is a page of its own.
    const query = parseInAppUrl(url).query;
    const ownKeys = Object.keys(trail?.node?.queryParams || {});
    const exact = !!trail?.exact && Object.keys(query).every((key) => ownKeys.includes(key));
    if (exact && trail.node.type === 'link' && trail.node.context !== 'project' && !trail.node.external) {
      const existing = this.navigationState.findBookmark({menuId: trail.node.id});
      return {
        kind: 'menu', menuId: trail.node.id, label: trail.node.label, icon: trail.node.icon,
        bookmarked: !!existing, currentLabel: existing ? (existing.label || trail.node.label) : undefined,
      };
    }
    const normalized = normalizeInAppUrl(url);
    if (!normalized) return {kind: null, bookmarked: false};
    // "Acme › Campaigns › Spring launch" - the breadcrumb without its "Projects" root.
    const labels = this.lastCrumbs.map((crumb) => crumb.label);
    if (labels.length > 1 && trail?.node && labels[0] === 'Projects') labels.shift();
    const existing = this.navigationState.findBookmark({url: normalized});
    return {
      kind: 'page', url: normalized, label: labels.join(' › ') || normalized, icon: trail?.node?.icon || null,
      bookmarked: !!existing, currentLabel: existing ? existing.label : undefined,
    };
  }

  private updateDocumentTitle(crumbs: NavCrumb[]): void {
    if (!crumbs.length) {
      this.title.setTitle(DEFAULT_TITLE);
      return;
    }
    const parts = crumbs.map((crumb) => crumb.label).reverse().slice(0, 2);
    this.title.setTitle([...parts, 'Snöball'].join(' · '));
  }
}
